import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import {
  FileText, Settings as SettingsIcon, LogOut, TrendingUp, Save,
  Palette, DollarSign, Plus, Trash2, Plug, Info, AlertTriangle, FileCheck,
  Upload, X, ChevronDown, User, Building2, Bell, Mail, Clock
} from "lucide-react";
import DocuSignDocuments from "@/pages/DocuSignDocuments";
import Sidebar from "@/components/Sidebar";

// Helper function to determine if a color is light or dark
const isLightColor = (hexColor) => {
  // Remove # if present
  const hex = hexColor.replace('#', '');
  // Convert to RGB
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  // Calculate luminance (perceived brightness)
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.5;
};

const Settings = () => {
  const { user, logout, token, updateUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") || "general";
  const [loading, setLoading] = useState(false);
  const [feeLoading, setFeeLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [settings, setSettings] = useState({
    company_name: user?.company_name || "",
    company_website: user?.company_website || "",
    logo_url: user?.logo_url || "",
    discovery_link: user?.discovery_link || "",
    first_meeting_link: user?.first_meeting_link || "",
    onboarding_link: user?.onboarding_link || "",
    theme: user?.theme || "light",
    brand_colors: user?.brand_colors || {
      primary: "#0f392b",
      secondary: "#f5f2eb",
      accent: "#c1666b"
    },
    // Advisor profile fields
    first_name: user?.first_name || "",
    last_name: user?.last_name || "",
    title: user?.title || "",
    designations: user?.designations || "",
    phone: user?.phone || "",
    contact_email: user?.contact_email || user?.email || "",
    broker_check_link: user?.broker_check_link || "",
    planning_software_link: user?.planning_software_link || "",
    secure_file_share_link: user?.secure_file_share_link || "",
    profile_picture_url: user?.profile_picture_url || ""
  });
  const [uploadingProfilePic, setUploadingProfilePic] = useState(false);

  const [feeSchedule, setFeeSchedule] = useState({
    fee_type: "flat",
    flat_rate: 1.0,
    tiers: [
      { min_amount: 0, max_amount: 500000, rate: 1.0 },
      { min_amount: 500001, max_amount: 1000000, rate: 0.85 },
      { min_amount: 1000001, max_amount: null, rate: 0.75 }
    ],
    display_preference: "annual_only"
  });

  // Reminder settings
  const [reminderSettings, setReminderSettings] = useState({
    reminders_enabled: false,
    reminder_mode: "notify",
    email_sender: "system",
    not_viewed: {
      enabled: false,
      interval_days: 7,
      max_reminders: 2
    },
    no_action: {
      enabled: false,
      interval_days: 7,
      max_reminders: 2
    }
  });
  const [reminderLoading, setReminderLoading] = useState(false);
  const [emailConnected, setEmailConnected] = useState(false);

  // Helper to check if a section has data filled (check user object directly for initial load)
  const sectionHasData = (section) => {
    switch (section) {
      case 'profile':
        return !!(user?.first_name && user?.last_name);
      case 'company':
        return !!(user?.company_name);
      case 'branding':
        // Consider branding as "has data" if user has any brand colors set
        return !!(user?.brand_colors);
      case 'fees':
        return false; // Fee schedule checked separately
      case 'danger':
        return false; // Always collapsed by default
      default:
        return false;
    }
  };

  // Collapsible section states - load from localStorage or default to all expanded for new users
  const [openSections, setOpenSections] = useState(() => {
    const saved = localStorage.getItem('settings_sections_state');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch {
        // Fallback to all expanded
      }
    }
    // Default all expanded for new users
    return { profile: true, company: true, branding: true, fees: true, danger: false };
  });

  const toggleSection = (section) => {
    setOpenSections(prev => {
      const newState = { ...prev, [section]: !prev[section] };
      // Save to localStorage - user's manual choice
      localStorage.setItem('settings_sections_state', JSON.stringify(newState));
      return newState;
    });
  };

  const headers = { Authorization: `Bearer ${token}` };

  // Sync settings when user data is loaded/updated
  useEffect(() => {
    if (user) {
      setSettings({
        company_name: user.company_name || "",
        company_website: user.company_website || "",
        logo_url: user.logo_url || "",
        discovery_link: user.discovery_link || "",
        first_meeting_link: user.first_meeting_link || "",
        onboarding_link: user.onboarding_link || "",
        theme: user.theme || "light",
        brand_colors: user.brand_colors || {
          primary: "#0f392b",
          secondary: "#f5f2eb",
          accent: "#c1666b"
        },
        first_name: user.first_name || "",
        last_name: user.last_name || "",
        title: user.title || "",
        designations: user.designations || "",
        phone: user.phone || "",
        contact_email: user.contact_email || user.email || "",
        broker_check_link: user.broker_check_link || "",
        planning_software_link: user.planning_software_link || "",
        secure_file_share_link: user.secure_file_share_link || "",
        profile_picture_url: user.profile_picture_url || ""
      });
    }
  }, [user]);

  useEffect(() => {
    loadFeeSchedule();
    loadReminderSettings();
    checkEmailConnection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkEmailConnection = async () => {
    try {
      const response = await axios.get(`${API}/integrations/status`, { headers, withCredentials: true });
      setEmailConnected(response.data?.email?.connected || false);
    } catch (error) {
      setEmailConnected(false);
    }
  };

  const loadReminderSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings/reminders`, { headers, withCredentials: true });
      if (response.data) {
        // Merge with defaults to ensure all nested objects exist
        setReminderSettings(prev => ({
          ...prev,
          ...response.data,
          not_viewed: {
            ...prev.not_viewed,
            ...(response.data.not_viewed || {})
          },
          no_action: {
            ...prev.no_action,
            ...(response.data.no_action || {})
          }
        }));
      }
    } catch (error) {
      // No reminder settings set yet, use defaults
    }
  };

  const saveReminderSettings = async () => {
    setReminderLoading(true);
    try {
      await axios.put(`${API}/settings/reminders`, reminderSettings, { headers, withCredentials: true });
      toast.success("Reminder settings saved");
    } catch (error) {
      toast.error("Failed to save reminder settings");
    } finally {
      setReminderLoading(false);
    }
  };

  // Format phone number to (XXX) XXX-XXXX
  const formatPhoneNumber = (phone) => {
    if (!phone) return "";
    // Remove all non-digits
    const digits = phone.replace(/\D/g, "");
    // Only format if we have 10 digits
    if (digits.length === 10) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    }
    // Return original if not 10 digits
    return phone;
  };

  const loadFeeSchedule = async () => {
    try {
      const response = await axios.get(`${API}/settings/fee-schedule`, { headers, withCredentials: true });
      if (response.data) {
        setFeeSchedule(response.data);
      }
    } catch (error) {
      // No fee schedule set yet, use defaults
    }
  };

  const saveSettings = async () => {
    setLoading(true);
    try {
      // Format phone before saving
      const formattedSettings = {
        ...settings,
        phone: formatPhoneNumber(settings.phone)
      };
      // Update local state with formatted phone
      setSettings(formattedSettings);
      
      const response = await axios.put(`${API}/settings/brand`, formattedSettings, { headers, withCredentials: true });
      updateUser(response.data);
      toast.success("Settings saved");
    } catch (error) {
      toast.error("Failed to save settings");
    } finally {
      setLoading(false);
    }
  };

  const handleProfilePictureUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/png', 'image/jpeg', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Please upload a PNG, JPEG, or WebP image");
      return;
    }

    // Validate file size (2MB max)
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Image too large. Maximum size is 2MB");
      return;
    }

    setUploadingProfilePic(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/settings/profile-picture`, formData, {
        headers: {
          ...headers,
          'Content-Type': 'multipart/form-data'
        },
        withCredentials: true
      });

      setSettings(prev => ({ ...prev, profile_picture_url: response.data.profile_picture_url }));
      updateUser({ ...user, profile_picture_url: response.data.profile_picture_url });
      toast.success("Profile picture uploaded!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload profile picture");
    } finally {
      setUploadingProfilePic(false);
    }
  };

  const handleRemoveProfilePicture = async () => {
    setUploadingProfilePic(true);
    try {
      await axios.delete(`${API}/settings/profile-picture`, { headers, withCredentials: true });
      setSettings(prev => ({ ...prev, profile_picture_url: "" }));
      updateUser({ ...user, profile_picture_url: null });
      toast.success("Profile picture removed");
    } catch (error) {
      toast.error("Failed to remove profile picture");
    } finally {
      setUploadingProfilePic(false);
    }
  };

  const saveFeeSchedule = async () => {
    setFeeLoading(true);
    try {
      await axios.put(`${API}/settings/fee-schedule`, feeSchedule, { headers, withCredentials: true });
      toast.success("Fee schedule saved");
    } catch (error) {
      toast.error("Failed to save fee schedule");
    } finally {
      setFeeLoading(false);
    }
  };

  const deleteAccount = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API}/auth/account`, { headers, withCredentials: true });
      toast.success("Account deleted successfully");
      logout();
      navigate("/");
    } catch (error) {
      toast.error("Failed to delete account");
      setDeleting(false);
    }
  };

  const addTier = () => {
    const tiers = feeSchedule.tiers;
    const lastTier = tiers[tiers.length - 1];
    // Get the min_amount of the last tier (the one with no limit)
    const lastTierMin = lastTier?.min_amount || 0;
    // Set the previous tier's max to be 500K above its min (or use existing logic)
    const newMaxForPreviousTier = lastTierMin + 500000;
    const newMin = newMaxForPreviousTier + 1; // Start $1 after previous tier ends
    
    setFeeSchedule({
      ...feeSchedule,
      tiers: [
        ...tiers.slice(0, -1),
        { ...lastTier, max_amount: newMaxForPreviousTier },
        { min_amount: newMin, max_amount: null, rate: Math.max(0.25, (lastTier?.rate || 1) - 0.15) }
      ]
    });
  };

  const removeTier = (index) => {
    if (feeSchedule.tiers.length <= 1) return;
    const newTiers = feeSchedule.tiers.filter((_, i) => i !== index);
    // Ensure last tier has no max
    if (newTiers.length > 0) {
      newTiers[newTiers.length - 1].max_amount = null;
    }
    setFeeSchedule({ ...feeSchedule, tiers: newTiers });
  };

  const updateTier = (index, field, value) => {
    const newTiers = [...feeSchedule.tiers];
    newTiers[index] = { ...newTiers[index], [field]: value };
    
    // Auto-adjust min of next tier when max changes (add $1)
    if (field === "max_amount" && index < newTiers.length - 1) {
      newTiers[index + 1].min_amount = value + 1;
    }
    
    setFeeSchedule({ ...feeSchedule, tiers: newTiers });
  };

  // Format number with commas
  const formatNumberWithCommas = (value) => {
    if (value === null || value === undefined || value === "") return "";
    return Number(value).toLocaleString();
  };

  // Parse formatted number back to number
  const parseFormattedNumber = (value) => {
    if (!value) return 0;
    return parseFloat(String(value).replace(/,/g, "")) || 0;
  };

  return (
    <div className="min-h-screen bg-sand" data-testid="settings-page">
      {/* Sidebar Component */}
      <Sidebar user={user} logout={logout} />

      {/* Main Content */}
      <main className="md:ml-52 lg:ml-64 p-4 md:p-8 pt-16 md:pt-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-display text-3xl font-bold text-forest">Settings</h1>
            <p className="text-muted-foreground mt-1">Customize your brand, fees, and preferences</p>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={(v) => setSearchParams({ tab: v })} className="space-y-6">
          <TabsList className="bg-stone/30 p-1">
            <TabsTrigger value="general" className="data-[state=active]:bg-forest data-[state=active]:text-white" data-testid="tab-general">
              <SettingsIcon className="w-4 h-4 mr-2" />
              General
            </TabsTrigger>
            <TabsTrigger value="documents" className="data-[state=active]:bg-forest data-[state=active]:text-white" data-testid="tab-documents">
              <FileCheck className="w-4 h-4 mr-2" />
              DocuSign Documents
            </TabsTrigger>
            <TabsTrigger value="reminders" className="data-[state=active]:bg-forest data-[state=active]:text-white" data-testid="tab-reminders">
              <Bell className="w-4 h-4 mr-2" />
              Follow-up Reminders
            </TabsTrigger>
          </TabsList>

          <TabsContent value="general">
        <div className="space-y-6">
          {/* Advisor Profile */}
          <Collapsible open={openSections.profile} onOpenChange={() => toggleSection('profile')}>
            <Card className="border-stone">
              <CollapsibleTrigger className="w-full">
                <CardHeader className="cursor-pointer hover:bg-sand/50 transition-colors rounded-t-lg">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <User className="w-5 h-5 text-forest" />
                      <div className="text-left">
                        <CardTitle className="font-display text-xl">Advisor Profile</CardTitle>
                        <CardDescription className="mt-1">Your personal information used in proposals and client communications</CardDescription>
                      </div>
                    </div>
                    <ChevronDown className={`w-5 h-5 text-muted-foreground transition-transform ${openSections.profile ? 'rotate-180' : ''}`} />
                  </div>
                </CardHeader>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent className="space-y-6 pt-0">
                  {/* Profile Picture Upload */}
                  <div>
                    <Label className="font-label text-sm uppercase tracking-wider text-muted-foreground">Profile Picture</Label>
                    <div className="mt-2 flex items-center gap-4">
                      <div className="relative">
                        {settings.profile_picture_url ? (
                          <img 
                            src={settings.profile_picture_url} 
                            alt="Profile" 
                            className="w-20 h-20 rounded-full object-cover border-2 border-forest/20"
                            data-testid="profile-picture-preview"
                          />
                        ) : (
                          <div className="w-20 h-20 rounded-full bg-forest/10 flex items-center justify-center border-2 border-dashed border-forest/30">
                            <User className="w-8 h-8 text-forest/50" />
                          </div>
                        )}
                      </div>
                      <div className="flex flex-col gap-2">
                        <label className="cursor-pointer">
                          <input
                            type="file"
                            accept="image/png,image/jpeg,image/webp"
                            onChange={handleProfilePictureUpload}
                            className="hidden"
                            data-testid="profile-picture-input"
                          />
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="pointer-events-none"
                            disabled={uploadingProfilePic}
                          >
                            <Upload className="w-4 h-4 mr-2" />
                            {uploadingProfilePic ? "Uploading..." : "Upload Photo"}
                          </Button>
                        </label>
                        {settings.profile_picture_url && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={handleRemoveProfilePicture}
                            disabled={uploadingProfilePic}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            data-testid="remove-profile-picture-btn"
                          >
                            <X className="w-4 h-4 mr-1" />
                            Remove
                          </Button>
                        )}
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">Appears on your contact card in proposals. PNG, JPEG, or WebP. Max 2MB.</p>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label className="font-label text-sm uppercase tracking-wider text-muted-foreground">
                        First Name <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        value={settings.first_name}
                        onChange={(e) => setSettings({...settings, first_name: e.target.value})}
                        placeholder="John"
                        className="mt-2"
                        data-testid="first-name-input"
                      />
                    </div>
                    <div>
                      <Label className="font-label text-sm uppercase tracking-wider text-muted-foreground">
                        Last Name <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        value={settings.last_name}
                        onChange={(e) => setSettings({...settings, last_name: e.target.value})}
                        placeholder="Smith"
                        className="mt-2"
                        data-testid="last-name-input"
                      />
                    </div>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label className="font-label text-sm uppercase tracking-wider text-muted-foreground">Title</Label>
                      <Input
                        value={settings.title}
                        onChange={(e) => setSettings({...settings, title: e.target.value})}
                        placeholder="Financial Advisor"
                        className="mt-2"
                        data-testid="title-input"
                      />
                    </div>
                    <div>
                      <Label className="font-label text-sm uppercase tracking-wider text-muted-foreground">Designations</Label>
                      <Input
                        value={settings.designations}
                        onChange={(e) => setSettings({...settings, designations: e.target.value})}
                        placeholder="CFP®, CFA, ChFC"
                        className="mt-2"
                        data-testid="designations-input"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="font-label text-sm uppercase tracking-wider text-muted-foreground">Phone Number</Label>
                      <Input
                        value={settings.phone}
                        onChange={(e) => setSettings({...settings, phone: e.target.value})}
                        placeholder="(555) 123-4567"
                        className="mt-2"
                        data-testid="phone-input"
                      />
                    </div>
                    <div>
                      <Label className="font-label text-sm uppercase tracking-wider text-muted-foreground">Contact Email</Label>
                      <Input
                        type="email"
                        value={settings.contact_email}
                        onChange={(e) => setSettings({...settings, contact_email: e.target.value})}
                        placeholder="you@company.com"
                        className="mt-2"
                        data-testid="contact-email-input"
                        autoComplete="off"
                      />
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground -mt-2">Contact email will appear on proposals and contact cards (your login email remains unchanged)</p>
                  <div>
                    <Label className="font-label text-sm uppercase tracking-wider text-muted-foreground">BrokerCheck Link</Label>
                    <Input
                      value={settings.broker_check_link}
                      onChange={(e) => setSettings({...settings, broker_check_link: e.target.value})}
                      placeholder="https://brokercheck.finra.org/individual/..."
                      className="mt-2"
                      data-testid="broker-check-input"
                    />
                    <p className="text-xs text-muted-foreground mt-1">FINRA BrokerCheck profile for your firm</p>
                  </div>
                  <div>
                    <Label className="font-label text-sm uppercase tracking-wider text-muted-foreground">Secure File Share Link</Label>
                    <Input
                      value={settings.secure_file_share_link}
                      onChange={(e) => setSettings({...settings, secure_file_share_link: e.target.value})}
                      placeholder="https://sharefile.com/..."
                      className="mt-2"
                      data-testid="secure-file-share-input"
                    />
                    <p className="text-xs text-muted-foreground mt-1">Link for clients to securely upload documents</p>
                  </div>
                  <Button 
                    className="w-full bg-forest hover:bg-forest/90"
                    onClick={saveSettings}
                    disabled={loading || !settings.first_name || !settings.last_name}
                    data-testid="save-profile-btn"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    {loading ? "Saving..." : "Save Profile"}
                  </Button>
                </CardContent>
              </CollapsibleContent>
            </Card>
          </Collapsible>

          {/* Company Info & Brand Colors */}
          <div className="grid lg:grid-cols-2 gap-6">
            {/* Company Info */}
            <Collapsible open={openSections.company} onOpenChange={() => toggleSection('company')}>
              <Card className="border-stone">
                <CollapsibleTrigger className="w-full">
                  <CardHeader className="cursor-pointer hover:bg-sand/50 transition-colors rounded-t-lg">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Building2 className="w-5 h-5 text-forest" />
                        <CardTitle className="font-display text-xl text-left">Company Information</CardTitle>
                      </div>
                      <ChevronDown className={`w-5 h-5 text-muted-foreground transition-transform ${openSections.company ? 'rotate-180' : ''}`} />
                    </div>
                  </CardHeader>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <CardContent className="space-y-6 pt-0">
                    <div>
                      <Label className="font-label text-sm uppercase tracking-wider text-muted-foreground">Company Name</Label>
                      <Input
                        value={settings.company_name}
                        onChange={(e) => setSettings({...settings, company_name: e.target.value})}
                        placeholder="Your Company Name"
                        className="mt-2"
                        data-testid="company-name-input"
                      />
                    </div>
                    <div>
                      <Label className="font-label text-sm uppercase tracking-wider text-muted-foreground">Company Website</Label>
                      <Input
                        value={settings.company_website}
                        onChange={(e) => setSettings({...settings, company_website: e.target.value})}
                        placeholder="https://yourcompany.com"
                        className="mt-2"
                        data-testid="company-website-input"
                      />
                      <p className="text-xs text-muted-foreground mt-1">Your company's website URL</p>
                    </div>
                <div>
                  <Label className="font-label text-sm uppercase tracking-wider text-muted-foreground">Company Logo</Label>
                  <div className="mt-2">
                    {/* Show logo preview with change option if we have a logo */}
                    {settings.logo_url ? (
                      <div className="p-4 bg-sand rounded-lg">
                        <div className="flex items-center justify-between mb-3">
                          <img 
                            src={settings.logo_url} 
                            alt="Logo preview"
                            className="h-16 object-contain"
                            onError={(e) => e.target.style.display = 'none'}
                          />
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setSettings({...settings, logo_url: ''});
                              updateUser({...user, logo_url: ''});
                            }}
                            className="text-muted-foreground hover:text-red-600 hover:border-red-300"
                          >
                            <X className="w-4 h-4 mr-1" />
                            Remove
                          </Button>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {settings.logo_url.startsWith('data:') ? 'Uploaded file' : 'External URL'}
                        </p>
                      </div>
                    ) : (
                      /* Show both options when no logo */
                      <div className="space-y-4 p-4 border border-dashed border-stone-300 rounded-lg">
                        {/* Upload Option */}
                        <div>
                          <input
                            type="file"
                            id="logo-upload"
                            accept="image/png,image/jpeg,image/gif,image/svg+xml,image/webp"
                            className="hidden"
                            onChange={async (e) => {
                              const file = e.target.files?.[0];
                              if (!file) return;
                              
                              if (file.size > 2 * 1024 * 1024) {
                                toast.error("File too large. Maximum size is 2MB");
                                return;
                              }
                              
                              const formData = new FormData();
                              formData.append('file', file);
                              
                              try {
                                setLoading(true);
                                const response = await axios.post(`${API}/settings/logo`, formData, {
                                  headers: { ...headers, 'Content-Type': 'multipart/form-data' },
                                  withCredentials: true
                                });
                                setSettings({...settings, logo_url: response.data.logo_url});
                                updateUser({...user, logo_url: response.data.logo_url});
                                toast.success("Logo uploaded successfully");
                              } catch (error) {
                                toast.error(error.response?.data?.detail || "Failed to upload logo");
                              } finally {
                                setLoading(false);
                              }
                            }}
                            data-testid="logo-file-input"
                          />
                          <Button 
                            type="button" 
                            variant="outline" 
                            onClick={() => document.getElementById('logo-upload').click()}
                            disabled={loading}
                            className="w-full justify-center"
                          >
                            <Upload className="w-4 h-4 mr-2" />
                            Upload Logo
                          </Button>
                          <p className="text-xs text-muted-foreground text-center mt-1">PNG, JPG, GIF, SVG, WebP (max 2MB)</p>
                        </div>
                        
                        <div className="flex items-center gap-3">
                          <div className="flex-1 h-px bg-stone-200"></div>
                          <span className="text-xs text-muted-foreground">or</span>
                          <div className="flex-1 h-px bg-stone-200"></div>
                        </div>
                        
                        {/* URL Option */}
                        <div>
                          <Input
                            value={settings.logo_url || ''}
                            onChange={(e) => setSettings({...settings, logo_url: e.target.value})}
                            placeholder="Paste logo URL (https://...)"
                            data-testid="logo-url-input"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <div>
                  <Label className="font-label text-sm uppercase tracking-wider text-muted-foreground">Discovery Call Link</Label>
                  <Input
                    value={settings.discovery_link}
                    onChange={(e) => setSettings({...settings, discovery_link: e.target.value})}
                    placeholder="https://calendly.com/your-discovery-call"
                    className="mt-2"
                    data-testid="discovery-link-input"
                  />
                  <p className="text-xs text-muted-foreground mt-1">Used on proposals for prospects to schedule initial meetings</p>
                </div>
                <div>
                  <Label className="font-label text-sm uppercase tracking-wider text-muted-foreground">First Client Meeting Link</Label>
                  <Input
                    value={settings.first_meeting_link}
                    onChange={(e) => setSettings({...settings, first_meeting_link: e.target.value})}
                    placeholder="https://calendly.com/your-first-meeting"
                    className="mt-2"
                    data-testid="first-meeting-link-input"
                  />
                  <p className="text-xs text-muted-foreground mt-1">Sent to new clients after acceptance to schedule their first meeting</p>
                </div>
                <div>
                  <Label className="font-label text-sm uppercase tracking-wider text-muted-foreground">Onboarding Link</Label>
                  <Input
                    value={settings.onboarding_link}
                    onChange={(e) => setSettings({...settings, onboarding_link: e.target.value})}
                    placeholder="https://your-software.com/onboarding"
                    className="mt-2"
                    data-testid="onboarding-link-input"
                  />
                  <p className="text-xs text-muted-foreground mt-1">Self-service onboarding portal for new clients</p>
                </div>
                <Button 
                  className="w-full bg-forest hover:bg-forest/90"
                  onClick={saveSettings}
                  disabled={loading}
                  data-testid="save-settings-btn"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {loading ? "Saving..." : "Save Company Settings"}
                </Button>
              </CardContent>
            </CollapsibleContent>
            </Card>
          </Collapsible>

            {/* Brand Colors */}
            <Collapsible open={openSections.branding} onOpenChange={() => toggleSection('branding')}>
              <Card className="border-stone">
                <CollapsibleTrigger className="w-full">
                  <CardHeader className="cursor-pointer hover:bg-sand/50 transition-colors rounded-t-lg">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Palette className="w-5 h-5 text-forest" />
                        <CardTitle className="font-display text-xl text-left">Brand Colors</CardTitle>
                      </div>
                      <ChevronDown className={`w-5 h-5 text-muted-foreground transition-transform ${openSections.branding ? 'rotate-180' : ''}`} />
                    </div>
                  </CardHeader>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <CardContent className="pt-0">
                <div className="space-y-4">
                  <div>
                    <Label className="font-label text-sm uppercase tracking-wider text-muted-foreground">Primary Color</Label>
                    <div className="flex items-center gap-3 mt-2">
                      <input
                        type="color"
                        value={settings.brand_colors.primary}
                        onChange={(e) => setSettings({
                          ...settings,
                          brand_colors: {...settings.brand_colors, primary: e.target.value}
                        })}
                        className="w-12 h-12 rounded-lg cursor-pointer border border-stone"
                      />
                      <Input
                        value={settings.brand_colors.primary}
                        onChange={(e) => setSettings({
                          ...settings,
                          brand_colors: {...settings.brand_colors, primary: e.target.value}
                        })}
                        className="font-mono"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="font-label text-sm uppercase tracking-wider text-muted-foreground">Secondary Color</Label>
                    <div className="flex items-center gap-3 mt-2">
                      <input
                        type="color"
                        value={settings.brand_colors.secondary}
                        onChange={(e) => setSettings({
                          ...settings,
                          brand_colors: {...settings.brand_colors, secondary: e.target.value}
                        })}
                        className="w-12 h-12 rounded-lg cursor-pointer border border-stone"
                      />
                      <Input
                        value={settings.brand_colors.secondary}
                        onChange={(e) => setSettings({
                          ...settings,
                          brand_colors: {...settings.brand_colors, secondary: e.target.value}
                        })}
                        className="font-mono"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="font-label text-sm uppercase tracking-wider text-muted-foreground">Accent Color</Label>
                    <div className="flex items-center gap-3 mt-2">
                      <input
                        type="color"
                        value={settings.brand_colors.accent}
                        onChange={(e) => setSettings({
                          ...settings,
                          brand_colors: {...settings.brand_colors, accent: e.target.value}
                        })}
                        className="w-12 h-12 rounded-lg cursor-pointer border border-stone"
                      />
                      <Input
                        value={settings.brand_colors.accent}
                        onChange={(e) => setSettings({
                          ...settings,
                          brand_colors: {...settings.brand_colors, accent: e.target.value}
                        })}
                        className="font-mono"
                      />
                    </div>
                  </div>
                </div>

                {/* Preview */}
                <div className="mt-6 p-4 rounded-lg border border-stone">
                  <p className="text-xs uppercase tracking-wider text-muted-foreground mb-3">Preview</p>
                  <div 
                    className="p-4 rounded-lg"
                    style={{ backgroundColor: settings.brand_colors.secondary }}
                  >
                    <h3 
                      className="font-display text-lg font-bold mb-2"
                      style={{ color: settings.brand_colors.primary }}
                    >
                      Sample Proposal
                    </h3>
                    <button
                      className="px-4 py-2 rounded-md text-sm font-medium"
                      style={{ 
                        backgroundColor: settings.brand_colors.accent,
                        color: isLightColor(settings.brand_colors.accent) ? '#000000' : '#ffffff'
                      }}
                    >
                      Accept
                    </button>
                  </div>
                </div>
              </CardContent>
            </CollapsibleContent>
            </Card>
          </Collapsible>
          </div>

          {/* Fee Schedule Section */}
          <Collapsible open={openSections.fees} onOpenChange={() => toggleSection('fees')}>
            <Card className="border-stone">
              <CollapsibleTrigger className="w-full">
                <CardHeader className="cursor-pointer hover:bg-sand/50 transition-colors rounded-t-lg">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <DollarSign className="w-5 h-5 text-forest" />
                      <div className="text-left">
                        <CardTitle className="font-display text-xl">AUM Fee Schedule</CardTitle>
                        <CardDescription className="mt-1">Configure your advisory fee structure for AUM-based proposals</CardDescription>
                      </div>
                    </div>
                    <ChevronDown className={`w-5 h-5 text-muted-foreground transition-transform ${openSections.fees ? 'rotate-180' : ''}`} />
                  </div>
                </CardHeader>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent className="space-y-8 pt-0">
              {/* Fee Type Selection */}
              <div>
                <Label className="font-label text-sm uppercase tracking-wider text-muted-foreground mb-4 block">
                  How do you calculate your advisory fees?
                </Label>
                <RadioGroup 
                  value={feeSchedule.fee_type} 
                  onValueChange={(value) => setFeeSchedule({...feeSchedule, fee_type: value})}
                  className="space-y-4"
                >
                  <div className="flex items-start space-x-3 p-4 rounded-lg border border-stone hover:border-forest/30 transition-colors">
                    <RadioGroupItem value="flat" id="flat" className="mt-1" />
                    <div className="flex-1">
                      <Label htmlFor="flat" className="font-medium text-forest cursor-pointer">Flat</Label>
                      <p className="text-sm text-muted-foreground mt-1">
                        A single rate is applied to the entire portfolio, regardless of size
                      </p>
                      <p className="text-xs text-muted-foreground mt-1 italic">
                        Example: $1M at 1% = $10,000/year
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-3 p-4 rounded-lg border border-stone hover:border-forest/30 transition-colors">
                    <RadioGroupItem value="graduated" id="graduated" className="mt-1" />
                    <div className="flex-1">
                      <Label htmlFor="graduated" className="font-medium text-forest cursor-pointer">Graduated</Label>
                      <p className="text-sm text-muted-foreground mt-1">
                        Fees are calculated as a blended rate based on multiple tiers
                      </p>
                      <p className="text-xs text-muted-foreground mt-1 italic">
                        Example: First $500K at 1%, next $500K at 0.85% → $1M total = $5,000 + $4,250 = $9,250/year
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-3 p-4 rounded-lg border border-stone hover:border-forest/30 transition-colors">
                    <RadioGroupItem value="cliff" id="cliff" className="mt-1" />
                    <div className="flex-1">
                      <Label htmlFor="cliff" className="font-medium text-forest cursor-pointer">Cliff</Label>
                      <p className="text-sm text-muted-foreground mt-1">
                        Once a portfolio reaches the next pricing tier, the new rate applies retroactively to the entire portfolio
                      </p>
                      <p className="text-xs text-muted-foreground mt-1 italic">
                        Example: $1M qualifies for 0.85% tier → $1M × 0.85% = $8,500/year
                      </p>
                    </div>
                  </div>
                </RadioGroup>
              </div>

              {/* Rate Entry */}
              <div>
                <Label className="font-label text-sm uppercase tracking-wider text-muted-foreground mb-4 block">
                  {feeSchedule.fee_type === "flat" ? "Your Rate" : "Your Fee Tiers"}
                </Label>
                
                {feeSchedule.fee_type === "flat" ? (
                  <div className="flex items-center gap-3 max-w-xs">
                    <Input
                      type="number"
                      step="0.01"
                      value={feeSchedule.flat_rate}
                      onChange={(e) => setFeeSchedule({...feeSchedule, flat_rate: parseFloat(e.target.value) || 0})}
                      className="font-mono"
                      data-testid="flat-rate-input"
                    />
                    <span className="text-muted-foreground font-medium">%</span>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="grid grid-cols-12 gap-3 text-xs font-label uppercase tracking-wider text-muted-foreground px-2">
                      <div className="col-span-4">From</div>
                      <div className="col-span-4">To</div>
                      <div className="col-span-3">Rate</div>
                      <div className="col-span-1"></div>
                    </div>
                    
                    {feeSchedule.tiers.map((tier, index) => (
                      <div key={index} className="grid grid-cols-12 gap-3 items-center">
                        <div className="col-span-4 relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                          <Input
                            type="text"
                            value={formatNumberWithCommas(tier.min_amount)}
                            onChange={(e) => updateTier(index, "min_amount", parseFormattedNumber(e.target.value))}
                            className="font-mono pl-7"
                            disabled={index === 0}
                          />
                        </div>
                        <div className="col-span-4">
                          {tier.max_amount === null ? (
                            <div className="h-10 px-3 flex items-center text-muted-foreground bg-stone/30 rounded-md">
                              ∞ (No limit)
                            </div>
                          ) : (
                            <div className="relative">
                              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                              <Input
                                type="text"
                                value={formatNumberWithCommas(tier.max_amount)}
                                onChange={(e) => updateTier(index, "max_amount", parseFormattedNumber(e.target.value))}
                                className="font-mono pl-7"
                              />
                            </div>
                          )}
                        </div>
                        <div className="col-span-3 flex items-center gap-1">
                          <Input
                            type="number"
                            step="0.01"
                            value={tier.rate}
                            onChange={(e) => updateTier(index, "rate", parseFloat(e.target.value) || 0)}
                            className="font-mono"
                          />
                          <span className="text-muted-foreground">%</span>
                        </div>
                        <div className="col-span-1">
                          {feeSchedule.tiers.length > 1 && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => removeTier(index)}
                              className="h-8 w-8 text-destructive hover:text-destructive"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                    
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={addTier}
                      className="mt-2"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add Tier
                    </Button>
                  </div>
                )}
              </div>

              {/* Display Preference */}
              <div>
                <Label className="font-label text-sm uppercase tracking-wider text-muted-foreground mb-4 block">
                  How should fees appear to prospects?
                </Label>
                <RadioGroup 
                  value={feeSchedule.display_preference} 
                  onValueChange={(value) => setFeeSchedule({...feeSchedule, display_preference: value})}
                  className="space-y-3"
                >
                  <div className="flex items-center space-x-3 p-3 rounded-lg border border-stone hover:border-forest/30 transition-colors">
                    <RadioGroupItem value="annual_only" id="annual_only" />
                    <Label htmlFor="annual_only" className="flex-1 cursor-pointer">
                      <span className="font-medium">Annual fee only</span>
                      <span className="text-sm text-muted-foreground ml-2">(e.g., "$9,250/year")</span>
                    </Label>
                  </div>
                  
                  <div className="flex items-center space-x-3 p-3 rounded-lg border border-stone hover:border-forest/30 transition-colors">
                    <RadioGroupItem value="annual_with_rate" id="annual_with_rate" />
                    <Label htmlFor="annual_with_rate" className="flex-1 cursor-pointer">
                      <span className="font-medium">Annual fee + effective rate</span>
                      <span className="text-sm text-muted-foreground ml-2">(e.g., "$9,250/year at 0.925%")</span>
                    </Label>
                  </div>
                  
                  <div className="flex items-center space-x-3 p-3 rounded-lg border border-stone hover:border-forest/30 transition-colors">
                    <RadioGroupItem value="annual_with_calculation" id="annual_with_calculation" />
                    <Label htmlFor="annual_with_calculation" className="flex-1 cursor-pointer">
                      <span className="font-medium">Annual fee + calculation</span>
                      <span className="text-sm text-muted-foreground ml-2">(e.g., "$1M × 0.925% = $9,250/year")</span>
                    </Label>
                  </div>
                  
                  <div className="flex items-center space-x-3 p-3 rounded-lg border border-stone hover:border-forest/30 transition-colors">
                    <RadioGroupItem value="full_breakdown" id="full_breakdown" />
                    <Label htmlFor="full_breakdown" className="flex-1 cursor-pointer">
                      <span className="font-medium">Full breakdown</span>
                      <span className="text-sm text-muted-foreground ml-2">(shows tier schedule + how fee was calculated)</span>
                    </Label>
                  </div>
                </RadioGroup>
              </div>

              {/* Save Fee Schedule Button */}
              <Button 
                className="w-full bg-forest hover:bg-forest/90"
                onClick={saveFeeSchedule}
                disabled={feeLoading}
                data-testid="save-fee-schedule-btn"
              >
                <Save className="w-4 h-4 mr-2" />
                {feeLoading ? "Saving..." : "Save Fee Schedule"}
              </Button>
            </CardContent>
          </CollapsibleContent>
          </Card>
        </Collapsible>

          {/* Danger Zone */}
          <Collapsible open={openSections.danger} onOpenChange={() => toggleSection('danger')}>
            <Card className="border-red-200 bg-red-50/30">
              <CollapsibleTrigger className="w-full">
                <CardHeader className="cursor-pointer hover:bg-red-50/50 transition-colors rounded-t-lg">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <AlertTriangle className="w-5 h-5 text-red-700" />
                      <div className="text-left">
                        <CardTitle className="font-display text-xl text-red-700">Danger Zone</CardTitle>
                        <CardDescription className="text-red-600/80 mt-1">Irreversible actions that permanently affect your account</CardDescription>
                      </div>
                    </div>
                    <ChevronDown className={`w-5 h-5 text-red-400 transition-transform ${openSections.danger ? 'rotate-180' : ''}`} />
                  </div>
                </CardHeader>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent className="pt-0">
              <div className="space-y-4">
                <div className="p-4 border border-red-200 rounded-lg bg-white">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <h4 className="font-medium text-red-800">Delete Account</h4>
                      <p className="text-sm text-red-600/80 mt-1">
                        Permanently delete your account and all associated data. This action cannot be undone.
                      </p>
                    </div>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button 
                          variant="destructive" 
                          className="shrink-0"
                          data-testid="delete-account-btn"
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Delete Account
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle className="flex items-center gap-2 text-red-700">
                            <AlertTriangle className="w-5 h-5" />
                            Are you absolutely sure?
                          </AlertDialogTitle>
                          <AlertDialogDescription className="space-y-3">
                            <p>This action <strong>cannot be undone</strong>. This will permanently delete:</p>
                            <ul className="list-disc pl-5 space-y-1 text-sm">
                              <li>Your account and profile information</li>
                              <li>All proposals you've created</li>
                              <li>All attachments and documents</li>
                              <li>All integration connections</li>
                              <li>Your fee schedule and settings</li>
                            </ul>
                            <p className="font-medium text-red-600">
                              Your prospects will no longer be able to view any sent proposals.
                            </p>
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={deleteAccount}
                            disabled={deleting}
                            className="bg-red-600 hover:bg-red-700"
                            data-testid="confirm-delete-account-btn"
                          >
                            {deleting ? "Deleting..." : "Yes, delete my account"}
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              </div>
            </CardContent>
          </CollapsibleContent>
          </Card>
        </Collapsible>
        </div>
          </TabsContent>

          <TabsContent value="documents">
            <DocuSignDocuments />
          </TabsContent>

          <TabsContent value="reminders">
            <Card className="border-stone">
              <CardHeader>
                <div className="flex items-center gap-3">
                  <Bell className="w-5 h-5 text-forest" />
                  <div>
                    <CardTitle className="font-display text-xl">Follow-up Reminders</CardTitle>
                    <CardDescription className="mt-1">
                      Automatically follow up on proposals that haven't been viewed or acted upon
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-8">
                {/* Setup prompt when not enabled */}
                {!reminderSettings.reminders_enabled && (
                  <div className="flex items-start gap-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                    <Bell className="w-6 h-6 text-amber-500 mt-0.5" />
                    <div>
                      <p className="font-medium text-amber-800">Reminders not configured</p>
                      <p className="text-sm text-amber-700 mt-1">
                        Enable follow-up reminders to automatically stay on top of pending proposals. You can choose to email prospects directly or get notified to follow up manually.
                      </p>
                    </div>
                  </div>
                )}

                {/* Master Toggle */}
                <div className="flex items-center justify-between p-4 bg-sand/50 rounded-lg">
                  <div>
                    <Label className="text-base font-medium">Enable Follow-up Reminders</Label>
                    <p className="text-sm text-muted-foreground mt-1">
                      Turn on automatic follow-up reminders for your proposals
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={reminderSettings.reminders_enabled}
                      onChange={(e) => setReminderSettings(prev => ({ ...prev, reminders_enabled: e.target.checked }))}
                      className="sr-only peer"
                      data-testid="reminders-enabled-toggle"
                    />
                    <div className="w-11 h-6 bg-stone rounded-full peer peer-checked:bg-forest peer-focus:ring-2 peer-focus:ring-forest/20 after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-full"></div>
                  </label>
                </div>

                {reminderSettings.reminders_enabled && (
                  <>
                    {/* Reminder Mode */}
                    <div className="space-y-3">
                      <Label className="text-base font-medium">Reminder Mode</Label>
                      <RadioGroup
                        value={reminderSettings.reminder_mode}
                        onValueChange={(value) => setReminderSettings(prev => ({ ...prev, reminder_mode: value }))}
                        className="grid grid-cols-1 sm:grid-cols-2 gap-4"
                      >
                        <label className={`flex items-start gap-3 p-4 rounded-lg border cursor-pointer transition-colors ${reminderSettings.reminder_mode === 'auto' ? 'border-forest bg-forest/5' : 'border-stone hover:border-forest/50'}`}>
                          <RadioGroupItem value="auto" id="mode-auto" className="mt-1" />
                          <div>
                            <span className="font-medium">Auto-send to Prospect</span>
                            <p className="text-sm text-muted-foreground mt-1">
                              Automatically email the prospect with a follow-up reminder
                            </p>
                          </div>
                        </label>
                        <label className={`flex items-start gap-3 p-4 rounded-lg border cursor-pointer transition-colors ${reminderSettings.reminder_mode === 'notify' ? 'border-forest bg-forest/5' : 'border-stone hover:border-forest/50'}`}>
                          <RadioGroupItem value="notify" id="mode-notify" className="mt-1" />
                          <div>
                            <span className="font-medium">Notify Me to Follow Up</span>
                            <p className="text-sm text-muted-foreground mt-1">
                              Get a notification so you can follow up manually
                            </p>
                          </div>
                        </label>
                      </RadioGroup>
                    </div>

                    {/* Email Sender (only for auto mode) */}
                    {reminderSettings.reminder_mode === 'auto' && (
                      <div className="space-y-3">
                        <Label className="text-base font-medium">Send Emails From</Label>
                        <RadioGroup
                          value={reminderSettings.email_sender}
                          onValueChange={(value) => {
                            if (value === 'gmail' && !emailConnected) {
                              // Don't allow selecting personal email if not connected
                              return;
                            }
                            setReminderSettings(prev => ({ ...prev, email_sender: value }));
                          }}
                          className="grid grid-cols-1 sm:grid-cols-2 gap-4"
                        >
                          <label className={`relative flex items-start gap-3 p-4 rounded-lg border transition-colors ${
                            !emailConnected 
                              ? 'border-stone/50 bg-stone/10 cursor-not-allowed opacity-60' 
                              : reminderSettings.email_sender === 'gmail' 
                                ? 'border-forest bg-forest/5 cursor-pointer' 
                                : 'border-stone hover:border-forest/50 cursor-pointer'
                          }`}>
                            <RadioGroupItem value="gmail" id="sender-gmail" className="mt-1" disabled={!emailConnected} />
                            <div>
                              <span className="font-medium">My Email</span>
                              <p className="text-sm text-muted-foreground mt-1">
                                {emailConnected 
                                  ? "More personal - comes from your email address"
                                  : "Email not connected"}
                              </p>
                            </div>
                            {!emailConnected && (
                              <Link 
                                to="/integrations" 
                                className="absolute top-2 right-2 text-xs text-forest hover:underline"
                                onClick={(e) => e.stopPropagation()}
                              >
                                Connect →
                              </Link>
                            )}
                          </label>
                          <label className={`flex items-start gap-3 p-4 rounded-lg border cursor-pointer transition-colors ${reminderSettings.email_sender === 'system' ? 'border-forest bg-forest/5' : 'border-stone hover:border-forest/50'}`}>
                            <RadioGroupItem value="system" id="sender-system" className="mt-1" />
                            <div>
                              <span className="font-medium">System Email</span>
                              <p className="text-sm text-muted-foreground mt-1">
                                More reliable - sent via our email system
                              </p>
                            </div>
                          </label>
                        </RadioGroup>
                        
                        {!emailConnected && reminderSettings.email_sender === 'gmail' && (
                          <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm">
                            <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />
                            <p className="text-amber-800">
                              Your email isn't connected. Please{" "}
                              <Link to="/integrations" className="text-forest font-medium hover:underline">
                                connect your email
                              </Link>
                              {" "}or switch to System Email.
                            </p>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Not Viewed Trigger */}
                    <div className="space-y-4 p-4 border border-stone rounded-lg">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Mail className="w-4 h-4 text-muted-foreground" />
                          <Label className="text-base font-medium">Proposal Sent But Not Viewed</Label>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={reminderSettings.not_viewed.enabled}
                            onChange={(e) => setReminderSettings(prev => ({
                              ...prev,
                              not_viewed: { ...prev.not_viewed, enabled: e.target.checked }
                            }))}
                            className="sr-only peer"
                          />
                          <div className="w-9 h-5 bg-stone rounded-full peer peer-checked:bg-forest after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:after:translate-x-full"></div>
                        </label>
                      </div>
                      
                      {reminderSettings.not_viewed.enabled && (
                        <div className="grid sm:grid-cols-2 gap-4 pt-2">
                          <div className="space-y-2">
                            <Label className="text-sm">Send reminder after</Label>
                            <select
                              value={reminderSettings.not_viewed.interval_days}
                              onChange={(e) => setReminderSettings(prev => ({
                                ...prev,
                                not_viewed: { ...prev.not_viewed, interval_days: parseInt(e.target.value) }
                              }))}
                              className="w-full h-10 px-3 rounded-md border border-stone bg-background"
                            >
                              <option value={3}>3 days</option>
                              <option value={5}>5 days</option>
                              <option value={7}>7 days</option>
                              <option value={14}>14 days</option>
                              <option value={21}>21 days</option>
                              <option value={30}>30 days</option>
                            </select>
                          </div>
                          <div className="space-y-2">
                            <Label className="text-sm">Maximum reminders</Label>
                            <select
                              value={reminderSettings.not_viewed.max_reminders}
                              onChange={(e) => setReminderSettings(prev => ({
                                ...prev,
                                not_viewed: { ...prev.not_viewed, max_reminders: parseInt(e.target.value) }
                              }))}
                              className="w-full h-10 px-3 rounded-md border border-stone bg-background"
                            >
                              <option value={1}>1 reminder</option>
                              <option value={2}>2 reminders</option>
                              <option value={3}>3 reminders</option>
                            </select>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* No Action Trigger */}
                    <div className="space-y-4 p-4 border border-stone rounded-lg">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Clock className="w-4 h-4 text-muted-foreground" />
                          <Label className="text-base font-medium">Proposal Viewed But No Action</Label>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={reminderSettings.no_action.enabled}
                            onChange={(e) => setReminderSettings(prev => ({
                              ...prev,
                              no_action: { ...prev.no_action, enabled: e.target.checked }
                            }))}
                            className="sr-only peer"
                          />
                          <div className="w-9 h-5 bg-stone rounded-full peer peer-checked:bg-forest after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:after:translate-x-full"></div>
                        </label>
                      </div>
                      
                      {reminderSettings.no_action.enabled && (
                        <div className="grid sm:grid-cols-2 gap-4 pt-2">
                          <div className="space-y-2">
                            <Label className="text-sm">Send reminder after</Label>
                            <select
                              value={reminderSettings.no_action.interval_days}
                              onChange={(e) => setReminderSettings(prev => ({
                                ...prev,
                                no_action: { ...prev.no_action, interval_days: parseInt(e.target.value) }
                              }))}
                              className="w-full h-10 px-3 rounded-md border border-stone bg-background"
                            >
                              <option value={3}>3 days</option>
                              <option value={5}>5 days</option>
                              <option value={7}>7 days</option>
                              <option value={14}>14 days</option>
                              <option value={21}>21 days</option>
                              <option value={30}>30 days</option>
                            </select>
                          </div>
                          <div className="space-y-2">
                            <Label className="text-sm">Maximum reminders</Label>
                            <select
                              value={reminderSettings.no_action.max_reminders}
                              onChange={(e) => setReminderSettings(prev => ({
                                ...prev,
                                no_action: { ...prev.no_action, max_reminders: parseInt(e.target.value) }
                              }))}
                              className="w-full h-10 px-3 rounded-md border border-stone bg-background"
                            >
                              <option value={1}>1 reminder</option>
                              <option value={2}>2 reminders</option>
                              <option value={3}>3 reminders</option>
                            </select>
                          </div>
                        </div>
                      )}
                    </div>
                  </>
                )}

                {/* Email Template Customization - Only for "My Email" sender */}
                {reminderSettings.reminders_enabled && reminderSettings.reminder_mode === 'auto' && reminderSettings.email_sender === 'gmail' && (
                  <div className="space-y-4 p-4 border border-forest/30 rounded-lg bg-forest/5">
                    <div className="flex items-center gap-2">
                      <Mail className="w-4 h-4 text-forest" />
                      <Label className="text-base font-medium">Customize Email Templates</Label>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Personalize the reminder emails sent from your email. Use these variables: {"{prospect_name}"}, {"{proposal_title}"}, {"{proposal_link}"}, {"{advisor_name}"}, {"{company_name}"}
                    </p>
                    
                    {/* Not Viewed Email Template */}
                    <div className="space-y-3 p-3 bg-white rounded-lg border border-stone">
                      <Label className="text-sm font-medium text-forest">Not Viewed Reminder</Label>
                      <div className="space-y-2">
                        <Label className="text-xs text-muted-foreground">Subject Line</Label>
                        <Input
                          value={reminderSettings.custom_templates?.not_viewed_prospect?.subject || ""}
                          onChange={(e) => setReminderSettings(prev => ({
                            ...prev,
                            custom_templates: {
                              ...prev.custom_templates,
                              not_viewed_prospect: {
                                ...prev.custom_templates?.not_viewed_prospect,
                                subject: e.target.value
                              }
                            }
                          }))}
                          placeholder="Reminder: {proposal_title} from {company_name}"
                          className="text-sm"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs text-muted-foreground">Email Body</Label>
                        <textarea
                          value={reminderSettings.custom_templates?.not_viewed_prospect?.body || ""}
                          onChange={(e) => setReminderSettings(prev => ({
                            ...prev,
                            custom_templates: {
                              ...prev.custom_templates,
                              not_viewed_prospect: {
                                ...prev.custom_templates?.not_viewed_prospect,
                                body: e.target.value
                              }
                            }
                          }))}
                          placeholder={`Hi {prospect_name},

I wanted to follow up on the proposal I sent you: "{proposal_title}".

I know things get busy, but I didn't want this to slip through the cracks. You can review the proposal here:

{proposal_link}

Please let me know if you have any questions.

Best regards,
{advisor_name}
{company_name}`}
                          className="w-full h-40 px-3 py-2 rounded-md border border-stone bg-background text-sm resize-none"
                        />
                      </div>
                    </div>
                    
                    {/* No Action Email Template */}
                    <div className="space-y-3 p-3 bg-white rounded-lg border border-stone">
                      <Label className="text-sm font-medium text-forest">Viewed But No Action Reminder</Label>
                      <div className="space-y-2">
                        <Label className="text-xs text-muted-foreground">Subject Line</Label>
                        <Input
                          value={reminderSettings.custom_templates?.no_action_prospect?.subject || ""}
                          onChange={(e) => setReminderSettings(prev => ({
                            ...prev,
                            custom_templates: {
                              ...prev.custom_templates,
                              no_action_prospect: {
                                ...prev.custom_templates?.no_action_prospect,
                                subject: e.target.value
                              }
                            }
                          }))}
                          placeholder="Following up: {proposal_title}"
                          className="text-sm"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs text-muted-foreground">Email Body</Label>
                        <textarea
                          value={reminderSettings.custom_templates?.no_action_prospect?.body || ""}
                          onChange={(e) => setReminderSettings(prev => ({
                            ...prev,
                            custom_templates: {
                              ...prev.custom_templates,
                              no_action_prospect: {
                                ...prev.custom_templates?.no_action_prospect,
                                body: e.target.value
                              }
                            }
                          }))}
                          placeholder={`Hi {prospect_name},

I noticed you had a chance to look at the proposal "{proposal_title}" and wanted to check in.

Do you have any questions I can help address? Happy to schedule a call if helpful.

{proposal_link}

Best regards,
{advisor_name}
{company_name}`}
                          className="w-full h-40 px-3 py-2 rounded-md border border-stone bg-background text-sm resize-none"
                        />
                      </div>
                    </div>
                    
                    <p className="text-xs text-muted-foreground">
                      Leave blank to use default templates. Custom templates only apply when sending from your email.
                    </p>
                  </div>
                )}

                {/* Save Button */}
                <div className="flex justify-end pt-4 border-t border-stone">
                  <Button
                    onClick={saveReminderSettings}
                    disabled={reminderLoading}
                    className="bg-forest hover:bg-forest/90"
                    data-testid="save-reminder-settings-btn"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    {reminderLoading ? "Saving..." : "Save Reminder Settings"}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default Settings;
